import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Building2, Quote, ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";

function ytId(url) {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : "";
}

function StoryCard({ t, i }) {
  const [playing, setPlaying] = useState(false);
  const id = ytId(t.youtube_url);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all flex flex-col" data-testid={`ss-card-${i}`}>
      <div className="relative aspect-video bg-slate-900">
        {playing && id ? (
          <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
            title={`${t.name} story`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid={`ss-iframe-${i}`} />
        ) : (
          <>
            {t.cover_image && <img src={t.cover_image} alt={t.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
            {id && (
              <button onClick={() => setPlaying(true)} aria-label={`Play ${t.name}'s story`}
                className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-white text-[#17263B] flex items-center justify-center shadow-xl hover:scale-105 transition-transform" data-testid={`ss-play-${i}`}>
                <Play className="w-6 h-6 ml-0.5 fill-current" />
              </button>
            )}
            {t.duration && <span className="absolute bottom-2.5 right-2.5 rounded-md bg-black/75 text-white text-[10px] font-bold px-1.5 py-0.5">{t.duration}</span>}
            {t.featured && <span className="absolute top-2.5 left-2.5 rounded-full bg-[#FFC72C] text-[#17263B] text-[10px] font-extrabold px-2.5 py-1 uppercase tracking-wide">Featured</span>}
          </>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="font-extrabold text-slate-900">{t.name} {t.role && <span className="text-slate-400 font-semibold">· {t.role}</span>}</p>
        <p className="text-xs text-slate-500 mt-0.5">{t.program || t.program_completed}</p>
        {t.company && <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-1"><Building2 className="w-3.5 h-3.5 text-slate-400" /> {t.company}{t.location ? `, ${t.location}` : ""}</p>}
        {t.quote && <p className="flex gap-1.5 text-sm text-slate-600 leading-relaxed mt-3 flex-1"><Quote className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> {t.quote}</p>}
      </div>
    </div>
  );
}

export default function SuccessStoriesPage() {
  const [cms, setCms] = useState(null);
  useSEO({
    title: "Success Stories — Training Basket Learners",
    description: "Real learners. Real skills. Real career journeys. Watch how Training Basket learners built careers at top companies.",
  });
  useEffect(() => {
    api.get("/website/sections/home_content").then((r) => setCms(r.data)).catch(() => setCms({}));
  }, []);

  const items = (cms?.testimonials || [])
    .filter((t) => t.visible !== false && t.name)
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  return (
    <div className="min-h-screen bg-white" data-testid="success-stories-page">
      <section className="relative overflow-hidden border-b border-slate-100">
        <div className="absolute -right-24 -top-16 w-72 h-72 rounded-full bg-amber-100/60 blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors" data-testid="ss-back"><ArrowLeft className="w-4 h-4" /> Home</Link>
          <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-slate-500 mt-6"><span className="h-0.5 w-8 bg-[#F5B301]" /> Success Stories</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#17263B] mt-4 leading-tight">
            Careers that moved <span className="text-[#F5B301] italic">forward</span>
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-3 max-w-2xl">From building in-demand skills to landing dream jobs — watch and read how Training Basket learners grew their careers.</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ss-grid">
          {items.map((t, i) => <StoryCard key={t.name + i} t={t} i={i} />)}
        </div>
        {cms && items.length === 0 && <p className="text-center text-slate-400 py-16">Success stories coming soon.</p>}
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-3xl bg-[#17263B] p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Your story could be next</h2>
          <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-md mx-auto">Join 5000+ learners who switched careers with Training Basket's job-ready programs.</p>
          <Link to="/courses" className="inline-flex items-center gap-2 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] font-bold px-8 py-4 text-sm mt-6 transition-all hover:-translate-y-0.5" data-testid="ss-cta-courses">
            Explore Programs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
